import React from "react";
import { Navigate, Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Dashboard from "@/pages/Dashboard";
import EmployeeCockpit from "@/pages/EmployeeCockpit";

/**
 * Landing page router:
 * - BoxHero staff (admin / returns_manager) in aggregate mode → Employee Cockpit.
 * - When an admin is "acting as" a specific customer (viewAs != all) → the
 *   customer analytics Dashboard for that store.
 * - Everyone else → their customer Dashboard.
 * - Customers with an incomplete onboarding are sent to the welcome page on first
 *   landing (once per session); after that a banner keeps the checklist reachable.
 */
export default function Home() {
  const { isStaff, viewAs, account } = useAuth();
  const impersonating = viewAs && viewAs !== "all";
  if (isStaff && !impersonating) return <EmployeeCockpit />;

  const ob = account?.onboarding;
  const incomplete = ob?.enabled && !ob?.completed_at;
  const seen = typeof window !== "undefined" && sessionStorage.getItem("bh_welcome_seen");

  if (incomplete && !seen) return <Navigate to="/welcome" replace />;

  return (
    <>
      {incomplete && <OnboardingBanner />}
      <Dashboard />
    </>
  );
}

function OnboardingBanner() {
  return (
    <Link
      to="/welcome"
      data-testid="onboarding-banner"
      className="group flex items-center gap-3 bg-gradient-to-r from-bh-blue to-bh-navy-deep text-white rounded-xl px-5 py-3.5 mb-5 hover:shadow-md transition-shadow"
    >
      <Sparkles className="w-5 h-5 text-bh-orange shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm" style={{ fontFamily: "Manrope" }}>Finish setting up your account</p>
        <p className="text-white/70 text-xs">Add your payment method, review your agreement and connect your store.</p>
      </div>
      <span className="inline-flex items-center gap-1 text-sm font-semibold whitespace-nowrap">
        Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}
